import React, { useMemo, useState } from 'react';
import { Calendar, CheckCircle2, Filter, MessageCircle, Phone, Quote, Star } from 'lucide-react';
import { BEFORE_AFTER_CASES, CLINIC_INFO, TESTIMONIALS } from '../data/mock';

const monthIndex = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  setiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
};

const parseSpanishMonthYear = (value) => {
  if (!value) return 0;
  const [monthRaw, yearRaw] = value.trim().split(/\s+/);
  const month = monthIndex[monthRaw?.toLowerCase()] ?? 0;
  const year = Number(yearRaw) || 0;
  return year * 12 + month;
};

const TestimonialsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const filters = [
    { id: 'all', label: 'Todos' },
    { id: 'estetica', label: 'Estética' },
    { id: 'ortodoncia', label: 'Ortodoncia' },
    { id: 'implantes', label: 'Implantes' },
    { id: 'infantil', label: 'Infantil' },
    { id: 'urgencias', label: 'Urgencias' },
  ];

  const treatmentMap = {
    'Diseño de Sonrisa': 'estetica',
    Blanqueamiento: 'estetica',
    'Alineadores Invisibles': 'ortodoncia',
    Ortodoncia: 'ortodoncia',
    'Implante Dental': 'implantes',
    'Odontología Infantil': 'infantil',
    'Urgencia Dental': 'urgencias',
  };

  const sortedTestimonials = useMemo(() => {
    const items =
      activeFilter === 'all' ? [...TESTIMONIALS] : TESTIMONIALS.filter((t) => treatmentMap[t.treatment] === activeFilter);

    if (sortBy === 'rating') {
      items.sort((a, b) => b.rating - a.rating || parseSpanishMonthYear(b.date) - parseSpanishMonthYear(a.date));
      return items;
    }

    items.sort((a, b) => parseSpanishMonthYear(b.date) - parseSpanishMonthYear(a.date));
    return items;
  }, [activeFilter, sortBy]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const whatsappMessage = encodeURIComponent('Hola, vi los testimonios y me gustaría agendar una cita.');

  return (
    <div>
      {/* TODO: Si se conectan reseñas reales, mostrar fuente y enlace verificable (Google Maps). */}
      <section className="bg-white pt-10 pb-8">
        <div className="container">
          <div className="max-w-3xl">
            <p className="text-caption text-[var(--color-accent)] mb-3">Testimonios</p>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
              <div>
                <h1 className="text-h1 mb-3">Historias reales de sonrisas transformadas</h1>
                <p className="text-body-lg">
                  Conoce experiencias y resultados. Si quieres, agenda tu cita y armamos un plan a tu medida.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={CLINIC_INFO.calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary justify-center"
                >
                  <Calendar size={18} />
                  Agendar cita
                </a>
                <a href={CLINIC_INFO.mapsLink} target="_blank" rel="noopener noreferrer" className="btn btn-secondary justify-center">
                  Ver reseñas en Google
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-8">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl border border-[var(--border-light)] py-5 px-3">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                  ))}
                </div>
              </div>
              <p className="text-3xl font-bold text-[var(--text-primary)]">{CLINIC_INFO.googleRating}/5</p>
              <p className="text-sm text-[var(--text-muted)]">Calificación en Google</p>
            </div>
            <div className="bg-white rounded-xl border border-[var(--border-light)] py-5 px-3">
              <p className="text-3xl font-bold text-[var(--text-primary)] mb-2">{CLINIC_INFO.totalReviews}+</p>
              <p className="text-sm text-[var(--text-muted)]">Reseñas en Google</p>
            </div>
            <div className="bg-white rounded-xl border border-[var(--border-light)] py-5 px-3">
              <p className="text-3xl font-bold text-[var(--text-primary)] mb-2">{CLINIC_INFO.yearsExperience}+</p>
              <p className="text-sm text-[var(--text-muted)]">Años de experiencia</p>
            </div>
          </div>
          <p className="text-xs text-center text-[var(--text-muted)] mt-4">Basado en reseñas públicas de Google Maps</p>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <h2 className="text-h2">Lo que dicen nuestros pacientes</h2>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-[var(--text-muted)]" />
                <div className="flex gap-2 overflow-x-auto no-scrollbar py-1" role="group" aria-label="Filtros">
                  {filters.map((filter) => {
                    const active = activeFilter === filter.id;
                    return (
                      <button
                        key={filter.id}
                        type="button"
                        onClick={() => setActiveFilter(filter.id)}
                        aria-pressed={active}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 ${
                          active
                            ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white'
                            : 'bg-white border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                        }`}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="text-sm text-[var(--text-muted)] flex items-center gap-2">
                Ordenar:
                <select
                  className="input select py-2 px-3 text-sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Ordenar reseñas"
                >
                  <option value="recent">Más recientes</option>
                  <option value="rating">Mejor valoradas</option>
                </select>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedTestimonials.map((testimonial) => {
              const expanded = expandedIds.has(testimonial.id);
              return (
                <div key={testimonial.id} className="card p-6 h-full flex flex-col">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <Quote size={28} strokeWidth={1.5} className="text-[var(--color-accent)]" />
                    {testimonial.verified && (
                      <span className="text-xs px-2 py-1 rounded-full bg-[var(--bg-secondary)] text-[var(--text-muted)] border border-[var(--border-light)] flex items-center gap-1">
                        <CheckCircle2 size={14} className="text-green-600" />
                        Google
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={14} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
                    ))}
                  </div>

                  <p className={`text-body text-[var(--text-secondary)] leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>
                    {testimonial.quote}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(testimonial.id)}
                    className="text-sm text-[var(--color-accent)] hover:underline mt-3 self-start"
                    aria-expanded={expanded}
                  >
                    {expanded ? 'Ver menos' : 'Leer más'}
                  </button>

                  <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-[var(--text-primary)]">{testimonial.name}</p>
                      <p className="text-xs text-[var(--text-muted)]">{testimonial.treatment}</p>
                    </div>
                    <span className="text-xs text-[var(--text-muted)]">{testimonial.date}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {sortedTestimonials.length === 0 && (
            <div className="text-center py-12">
              <p className="text-body text-[var(--text-muted)]">No hay testimonios para este filtro.</p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)] py-12">
        <div className="container">
          <div className="max-w-3xl mb-10">
            <h2 className="text-h2 mb-4">Casos de transformación</h2>
            <p className="text-body-lg">
              Cada caso es distinto. Te mostramos ejemplos del tipo de cambios que buscamos con un plan bien indicado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BEFORE_AFTER_CASES.map((caseItem) => (
              <div key={caseItem.id} className="card overflow-hidden h-full">
                {/* TODO: Reemplazar imágenes con casos reales autorizados (antes/después). */}
                <div className="grid grid-cols-1 min-[360px]:grid-cols-2">
                  <div className="aspect-square relative">
                    <img src={caseItem.beforeImage} alt={`Antes - ${caseItem.title}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 text-white text-xs rounded-full">
                      Antes
                    </span>
                  </div>
                  <div className="aspect-square relative">
                    <img src={caseItem.afterImage} alt={`Después - ${caseItem.title}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                    <span className="absolute top-3 right-3 px-2.5 py-1 bg-[var(--color-accent)] text-white text-xs rounded-full">
                      Después
                    </span>
                  </div>
                </div>

                <div className="p-5 flex flex-col h-full">
                  <h3 className="font-semibold text-[var(--text-primary)] mb-2">{caseItem.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">{caseItem.description}</p>

                  <div className="flex items-center justify-between text-sm pt-4 border-t border-[var(--border-light)] mt-auto">
                    <span className="text-[var(--text-muted)]">{caseItem.treatment}</span>
                    <span className="text-[var(--text-muted)]">{caseItem.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-[var(--text-muted)] mt-6">
            <em>Los resultados pueden variar según las condiciones individuales de cada paciente.</em>
          </p>
        </div>
      </section>

      <section className="bg-[var(--color-accent-bg)] py-12">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-h2 mb-3">Agenda tu cita</h2>
            <p className="text-body-lg mb-2">Incluye: evaluación + diagnóstico + plan por escrito.</p>
            <p className="text-body-sm mb-8 text-[var(--text-muted)]">Da el primer paso y te guiamos con claridad.</p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={CLINIC_INFO.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                <Calendar size={20} />
                Agendar cita
              </a>
              <a
                href={`https://wa.me/${CLINIC_INFO.whatsapp}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp btn-lg"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
              <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-secondary btn-lg">
                <Phone size={20} />
                Llamar
              </a>
            </div>

            <p className="text-xs text-[var(--text-muted)] mt-4">
              Si tienes una urgencia, llamar suele ser la forma más rápida.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
